type THTTPMethods = 'GET' | 'POST';

interface IComponent {
  type: string;
  label?: string;
  name?: string;
  placeholder?: string;
  cta?: any;
  options?: string[];
  condition?: string;
}

interface IRequest {
  path: string;
  method: THTTPMethods;
  fields?: string[];
}

interface ITriggerTracking {
  phone?: string;
  email?: string;
  token: string;
  subCategoryIds?: string[] | string;
  action?: string;
  errorMessage?: string;
}

interface IStep {
  id: string;
  title: string;
  description: string;
  components: IComponent[];
  request?: IRequest;
  triggerTracking: (ITriggerTracking) => void;
}

const PHONE: IStep = {
  id: 'PHONE',
  title: 'Qual seu telefone?',
  description:
    'Seu número de celular é utilizado para entrar no aplicativo, dessa forma você não precisa de senha.',
  components: [
    {
      type: 'textfield_phone',
      name: 'phone',
      label: 'Celular',
      placeholder: 'Ex.: 11 99999-9999',
    },
    {
      type: 'button',
      label: 'Continuar',
    },
  ],
  request: {
    path: `${process.env.NEXT_PUBLIC_API_PRO}/signup/phone_occurrences`,
    method: 'GET',
  },
  triggerTracking: ({ phone, token }: ITriggerTracking) => {
    window.tagManager.custom({
      event: 'selfDescribingEvent',
      schema: 'iglu:br.com.getninjas/pro_sign_up/jsonschema/1-0-1',
      data: {
        step: 'insert_phone',
        action: 'click',
        state: phone || '',
        country: 'pt-BR',
        token,
      },
    });
  },
};

const EMAIL: IStep = {
  id: 'E-MAIL',
  title: 'Qual seu E-mail?',
  description:
    'Seu e-mail é importante para termos um canal de comunicação com você, quando necessário.',
  components: [
    {
      type: 'textfield',
      name: 'email',
      label: 'E-mail',
      placeholder: 'Digite seu e-mail',
    },
    {
      type: 'button',
      label: 'Continuar',
    },
  ],
  request: {
    path: `${process.env.NEXT_PUBLIC_API_PRO}/signup`,
    method: 'POST',
    fields: ['phone', 'email'],
  },
  triggerTracking: ({ email, token }: ITriggerTracking) => {
    window.tagManager.custom({
      event: 'selfDescribingEvent',
      schema: 'iglu:br.com.getninjas/pro_sign_up/jsonschema/1-0-1',
      data: {
        step: 'insert_email',
        action: email,
        country: 'pt-BR',
        token,
      },
    });
  },
};

const PASSWORD: IStep = {
  id: 'PASSWORD',
  title: '',
  description: '',
  components: [
    {
      type: 'password',
    },
  ],
  triggerTracking: () => {},
};

const CATEGORY: IStep = {
  id: 'CATEGORY',
  title: 'Escolha a categoria dos serviços que você realiza',
  description: 'Escolha a categoria dos serviços que você realiza',
  components: [
    {
      type: 'categories',
    },
    {
      type: 'button',
      label: 'Continuar',
    },
  ],
  request: {
    path: `${process.env.NEXT_PUBLIC_API_PRO}/signup/send_categories`,
    method: 'POST',
    fields: ['token', 'subCategoryIds'],
  },
  triggerTracking: ({ subCategoryIds, token }: ITriggerTracking) => {
    window.tagManager.custom({
      event: 'selfDescribingEvent',
      schema: 'iglu:br.com.getninjas/pro_sign_up/jsonschema/1-0-1',
      data: {
        step: 'insert_group',
        action: JSON.stringify(subCategoryIds),
        country: 'pt-BR',
        token,
      },
    });
  },
};

const PERSONAL_INFO: IStep = {
  id: 'PERSONAL_INFO',
  title: 'Informações pessoais',
  description:
    'Preencha o nome e o cep abaixo para enviarmos a nota fiscal para você',
  components: [
    {
      type: 'textfield',
      name: 'name',
      label: 'Nome completo',
      placeholder: 'Digite seu nome completo',
    },
    {
      type: 'textfield',
      name: 'cep',
      label: 'CEP',
      placeholder: 'Digite seu CEP',
    },
    {
      type: 'textfield_phone',
      name: 'phone',
      label: 'Celular',
      placeholder: 'Digite seu numero de celular',
    },
    {
      type: 'checkbox_cep',
      name: 'checkCep',
      label: 'Presto serviço na mesma região',
    },
    {
      type: 'textfield_cep',
      name: 'home_cep',
      label: 'CEP de atuação',
      placeholder: 'Digite seu CEP',
    },
    {
      type: 'button',
      label: 'Continuar',
    },
  ],
  request: {
    path: `${process.env.NEXT_PUBLIC_API_PRO}/signup/additional_info`,
    method: 'POST',
    fields: ['name', 'phone', 'cep', 'token'],
  },
  triggerTracking: ({ errorMessage, token }: ITriggerTracking) => {
    window.tagManager.custom({
      event: 'selfDescribingEvent',
      schema: 'iglu:br.com.getninjas/pro_sign_up/jsonschema/1-0-1',
      data: {
        step: 'insert_additional_info',
        action: errorMessage ? 'error_message' : 'click',
        state: errorMessage || '',
        country: 'pt-BR',
        token,
      },
    });
  },
};

const CODE_VALIDATION: IStep = {
  id: 'CODE_VALIDATION',
  title: '',
  description: '',
  components: [
    {
      type: 'pin',
    },
  ],
  request: {
    path: `${process.env.NEXT_PUBLIC_API_PRO}/signup/sms_validation`,
    method: 'POST',
    fields: ['pin', 'token'],
  },
  triggerTracking: ({ action, token }: ITriggerTracking) => {
    window.tagManager.custom({
      event: 'selfDescribingEvent',
      schema: 'iglu:br.com.getninjas/pro_sign_up/jsonschema/1-0-1',
      data: {
        step: 'phone_validation',
        action,
        country: 'pt-BR',
        token,
      },
    });
  },
};

const DOCUMENT: IStep = {
  id: 'DOCUMENT',
  title: 'Última etapa',
  description:
    'Confirme o nome e preencha o CPF/CNPJ abaixo para concluirmos o cadastro',
  components: [
    {
      type: 'textfield',
      name: 'name',
      label: 'Nome completo',
      placeholder: 'Digite seu nome completo',
    },
    {
      type: 'textfield',
      name: 'cpfOrCnpj',
      label: 'CPF ou CNPJ',
      placeholder: 'Digite seu cpf ou cnpj',
    },
    {
      type: 'datefield',
      name: 'birthday',
      label: 'Data de nascimento',
      placeholder: '',
      condition: 'CPF',
    },
    {
      type: 'selectfield',
      options: ['Masculino', 'Feminino', 'Prefiro não dizer'],
      name: 'gender',
      label: 'Gênero',
      placeholder: 'Selecione uma opção',
      condition: 'CPF',
    },
    {
      type: 'datefield',
      name: 'founded_at',
      label: 'Data de fundação',
      placeholder: '',
      condition: 'CNPJ',
    },
    {
      type: 'selectfield',
      options: [
        'Microempresa (até 9 funcionários)',
        'Pequena (10 a 49 funcionários)',
        'Média (50 a 99 funcionários)',
        'Grande (100 ou mais funcionários)',
      ],
      name: 'company_size',
      label: 'Tamanho da empresa',
      placeholder: 'Selecione uma opção',
      condition: 'CNPJ',
    },
    {
      type: 'checkbox_lgpd',
      label: 'Li e concordo com as',
      name: 'lgpd',
      cta: {
        text: 'Políticas de Privacidade',
        link: 'https://www.getninjas.com.br/politica-de-privacidade',
      },
    },
    {
      type: 'end_button',
      label: 'Finalizar cadastro',
    },
  ],
  request: {
    path: `${process.env.NEXT_PUBLIC_API_PRO}/signup/document`,
    method: 'POST',
    fields: [
      'name',
      'cpfOrCnpj',
      'birthday',
      'gender',
      'founded_at',
      'company_size',
      'token',
    ],
  },
  triggerTracking: ({ errorMessage, token }: ITriggerTracking) => {
    window.tagManager.custom({
      event: 'selfDescribingEvent',
      schema: 'iglu:br.com.getninjas/pro_sign_up/jsonschema/1-0-1',
      data: {
        step: 'insert_cpf_cnpj',
        action: errorMessage ? 'error_message' : 'click',
        state: errorMessage || '',
        country: 'pt-BR',
        token,
      },
    });
  },
};

const DOCUMENT_FOR_MFA = { ...DOCUMENT };
DOCUMENT_FOR_MFA.request.fields =
  DOCUMENT_FOR_MFA.request.fields.concat('password');

export const StepByStepData: IStep[] = [
  PHONE,
  EMAIL,
  CATEGORY,
  PERSONAL_INFO,
  CODE_VALIDATION,
  DOCUMENT,
];

export const StepByStepDataForMFA: IStep[] = [
  PHONE,
  EMAIL,
  PASSWORD,
  CATEGORY,
  PERSONAL_INFO,
  CODE_VALIDATION,
  DOCUMENT_FOR_MFA,
];
